import { isBenefitCategory, isDeductionCategory } from './categories';
import { ANNUAL_BONUS_PLAN_CODE } from './catalogData';
import { SUPPORTED_CALCULATION_TYPE, type BenefitPlanInput, type DeductionDefinitionInput, type TaxProfile } from './types';

/**
 * Field-level rules from docs/benefits-deductions-contract.md sections 4.1.1 and 4.2 that need no stored data. The forms run them for early,
 * plain-language field messages and the Mock gateways run the same functions, so the mock server and the screen agree. The real server is always the
 * authority and re-checks everything (and checks what only it can: duplicates, catalog types, tax profiles, versions, elections).
 */

export interface FieldIssue {
  field: string;
  message: string;
}

export const MAX_AMOUNT = 1_000_000;
export const SHORT_CODE_PATTERN = /^[A-Za-z0-9_-]{2,30}$/;

/** Trim, collapse spaces, case-insensitive: the key the server uses to refuse a duplicate name or short code. */
export function normalizeKey(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function isBlank(value: string | null | undefined): boolean {
  return (value ?? '').trim() === '';
}

function outOfRange(value: number | null | undefined, min: number, max: number): boolean {
  return value !== null && value !== undefined && (Number.isNaN(value) || value < min || value > max);
}

function present(value: number | null | undefined): boolean {
  return value !== null && value !== undefined;
}

const MONEY_MESSAGE = 'Enter an amount from 0 to 1,000,000.';
const PERCENT_MESSAGE = 'Enter a percent from 0 to 100.';

export const NAME_REQUIRED = 'Enter a name.';
export const SHORT_CODE_REQUIRED = 'Enter a short code of 2 to 30 letters, numbers, hyphens or underscores.';

export function validateBenefitPlanShape(input: BenefitPlanInput): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const add = (field: string, message: string) => issues.push({ field, message });

  if (isBlank(input.name)) add('name', NAME_REQUIRED);
  if (!SHORT_CODE_PATTERN.test((input.shortCode ?? '').trim())) add('shortCode', SHORT_CODE_REQUIRED);
  if (!isBenefitCategory(input.categoryCode)) add('categoryCode', 'Choose a category.');

  if (input.effectiveStartDate && input.effectiveEndDate && input.effectiveEndDate < input.effectiveStartDate) {
    add('effectiveEndDate', 'The end date cannot be before the start date.');
  }
  if (present(input.planYear) && (!Number.isInteger(input.planYear) || outOfRange(input.planYear, 2000, 2100))) add('planYear', 'Enter a plan year from 2000 to 2100.');
  if (present(input.waitingPeriodDays) && (!Number.isInteger(input.waitingPeriodDays) || outOfRange(input.waitingPeriodDays, 0, 365))) {
    add('waitingPeriodDays', 'Enter a waiting period from 0 to 365 days.');
  }
  for (const field of ['employeeContributionPercent', 'employerContributionPercent'] as const) {
    if (outOfRange(input[field], 0, 100)) add(field, PERCENT_MESSAGE);
  }
  for (const field of ['employeeContributionAmount', 'employerContributionAmount'] as const) {
    if (outOfRange(input[field], 0, MAX_AMOUNT)) add(field, MONEY_MESSAGE);
  }
  input.coverageTiers.forEach((tier, index) => {
    if (outOfRange(tier.employeeAmount, 0, MAX_AMOUNT) || outOfRange(tier.employerAmount, 0, MAX_AMOUNT)) add(`coverageTiers.${index}`, MONEY_MESSAGE);
  });

  const hasEmployeeAmount = present(input.employeeContributionAmount) || present(input.employeeContributionPercent);
  const hasAnyAmount = hasEmployeeAmount || present(input.employerContributionAmount) || present(input.employerContributionPercent);

  if (input.fundingModel === 'EmployerPaid' && hasEmployeeAmount) {
    add('fundingModel', 'An employer-paid benefit carries no employee amount or percent. Clear the employee amount, or choose a different funding model.');
  }
  if (input.contributionMethod === 'NoPayrollContribution' && (hasAnyAmount || input.coverageTiers.length > 0)) {
    add('contributionMethod', 'A benefit with no payroll contribution carries no amounts, percents or coverage tiers.');
  }
  if (input.contributionMethod === 'TieredByCoverage') {
    if (input.coverageTiers.length === 0) add('coverageTiers', 'Add at least one coverage tier.');
    const levels = input.coverageTiers.map((t) => t.coverageLevel);
    if (new Set(levels).size !== levels.length) add('coverageTiers', 'Each coverage level can appear only once.');
    if (present(input.employeeContributionAmount) || present(input.employerContributionAmount)) {
      add('contributionMethod', 'A coverage-level benefit takes its amounts from the tiers, not a single flat amount.');
    }
  } else if (input.coverageTiers.length > 0 && input.contributionMethod !== 'NoPayrollContribution') {
    add('coverageTiers', 'Coverage tiers apply only when the contribution depends on coverage level.');
  }

  if (input.categoryCode === 'leave-wellbeing') {
    if (input.contributionMethod !== 'NoPayrollContribution') add('contributionMethod', 'A leave benefit carries no payroll contribution. Time and Leave owns balances and accruals.');
    if (input.fundingModel !== 'NotApplicable') add('fundingModel', 'A leave benefit has no funding model. Choose "Not applicable".');
  }
  if (input.typeCode === ANNUAL_BONUS_PLAN_CODE && input.contributionMethod !== 'NoPayrollContribution') {
    add('contributionMethod', 'The Annual Bonus Plan carries no payroll contribution. The bonus is paid through an approved payroll earning code.');
  }
  return issues;
}

/** A plan whose contribution method creates a payroll amount. `NoPayrollContribution` needs no tax profile. */
export function hasPayrollContribution(method: BenefitPlanInput['contributionMethod']): boolean {
  return method !== 'NoPayrollContribution';
}

export const TAX_REVIEW_ISSUE = 'Tax treatment must be approved before this plan can be used in payroll. No approved tax profile is attached.';

export function validateDeductionDefinitionShape(input: DeductionDefinitionInput): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const add = (field: string, message: string) => issues.push({ field, message });

  if (isBlank(input.name)) add('name', NAME_REQUIRED);
  if (!SHORT_CODE_PATTERN.test((input.shortCode ?? '').trim())) add('shortCode', SHORT_CODE_REQUIRED);
  if (!isDeductionCategory(input.categoryCode)) add('categoryCode', 'Choose a category.');
  else if (input.categoryCode === 'taxes-statutory') add('categoryCode', 'Taxes and statutory deductions are system definitions. You cannot create one.');

  if (input.categoryCode === 'garnishments-levies') {
    if (input.classification !== 'Involuntary') add('classification', 'A garnishment or levy is an involuntary deduction.');
    if (input.calculationType !== 'ApprovedGarnishmentFormula') add('calculationType', 'A garnishment or levy uses the approved garnishment formula.');
  }

  const isPercent = input.calculationType === 'PercentOfEligibleGross' || input.calculationType === 'PercentOfDisposableEarnings'
    || input.calculationType === 'ApprovedRetirementElectionPercent';
  if (isPercent ? outOfRange(input.defaultAmount, 0, 100) : outOfRange(input.defaultAmount, 0, MAX_AMOUNT)) {
    add('defaultAmount', isPercent ? PERCENT_MESSAGE : MONEY_MESSAGE);
  }
  if (outOfRange(input.defaultCompanyContribution, 0, MAX_AMOUNT)) add('defaultCompanyContribution', MONEY_MESSAGE);
  for (const field of ['annualLimit', 'perPayPeriodLimit', 'goalBalance'] as const) {
    if (outOfRange(input[field], 0, MAX_AMOUNT)) add(field, MONEY_MESSAGE);
  }
  if (present(input.priority) && (!Number.isInteger(input.priority) || outOfRange(input.priority, 1, 99))) add('priority', 'Enter a priority from 1 to 99. Lower numbers come first.');
  if (input.effectiveStartDate && input.effectiveEndDate && input.effectiveEndDate < input.effectiveStartDate) {
    add('effectiveEndDate', 'The end date cannot be before the start date.');
  }
  return issues;
}

export const GARNISHMENT_RULE_ISSUE = 'Garnishment limits and priorities are controlled by applicable rules; rule review required';
export const TAX_REVIEW_DEDUCTION_ISSUE = 'Tax treatment has not been reviewed. Choose an approved tax profile before this deduction can be activated';
export const CALCULATION_NOT_SUPPORTED_ISSUE = 'This calculation type is not supported by payroll yet, so the deduction cannot be activated';

export function isApprovedAndEffective(profile: TaxProfile | undefined, asOf: string): boolean {
  if (!profile || !profile.approvedForPayroll || profile.status !== 'Approved') return false;
  if (profile.effectiveFrom && profile.effectiveFrom > asOf) return false;
  if (profile.effectiveTo && profile.effectiveTo < asOf) return false;
  return true;
}

export function isFixedAmount(calculationType: DeductionDefinitionInput['calculationType'] | null | undefined): boolean {
  return calculationType === SUPPORTED_CALCULATION_TYPE;
}
